import React, { useEffect } from "react";
import { Button, Grid, Group, Textarea, Loader, Center } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import { useUser } from "hooks/useUser";
import { ProjectResponse } from "api/projectDetail/getProjectDetail";
import { useUpdateProject } from "api/projectDetail/updateProjectDetail";
import { useNavigate, useParams } from "react-router-dom";

const clientDashboardFormSchema = z.object({
  name: z.string().trim().min(1, { message: "Please enter project name" }),
  description: z
    .string()
    .trim()
    .min(1, { message: "Please enter description" }),
  category: z.string().trim().min(1, { message: "Please enter category" }),
  // clientId: z.string().trim().min(1, { message: "Please enter client ID" }),
  projectOverview: z
    .string()
    .trim()
    .min(1, { message: "Please enter project overview" }),
  projectObjectives: z
    .string()
    .trim()
    .min(1, { message: "Please enter project objectives" }),
  scopeOfWork: z
    .string()
    .trim()
    .min(1, { message: "Please enter scope of work" }),
  inclusions: z.string().trim().min(1, { message: "Please enter inclusions" }),
  exclusions: z.string().trim().min(1, { message: "Please enter exclusions" }),
  assumptions: z
    .string()
    .trim()
    .min(1, { message: "Please enter assumptions" }),
  constraints: z
    .string()
    .trim()
    .min(1, { message: "Please enter constraints" }),
  stakeholdersInfo: z
    .string()
    .trim()
    .min(1, { message: "Please enter stakeholders info" }),
  timelineMilestones: z
    .string()
    .trim()
    .min(1, { message: "Please enter timeline & milestones" }),
  successCriteria: z
    .string()
    .trim()
    .min(1, { message: "Please enter success criteria" }),
  risksMitigation: z
    .string()
    .trim()
    .min(1, { message: "Please enter risks & mitigation" }),
  changeManagementProcess: z
    .string()
    .trim()
    .min(1, { message: "Please enter change management process" }),
  approvals: z.string().trim().min(1, { message: "Please enter approvals" }),
});

type Props = {
  data?: ProjectResponse;
  isLoading: boolean;
};

export default function ClientDashboardForm({ data, isLoading }: Props) {
  const { user } = useUser();
  const { projectId } = useParams<{ projectId: string }>();
  const { mutate: updateProject } = useUpdateProject();
  const navigate = useNavigate();

  const form = useForm({
    initialValues: {
      name: data?.name || "",
      description: data?.description || "",
      category: data?.category || "",
      clientId: data?.client || "",
      projectOverview: data?.projectOverview || "",
      projectObjectives: data?.projectObjectives || "",
      scopeOfWork: data?.scopeOfWork || "",
      inclusions: data?.inclusions || "",
      exclusions: data?.exclusions || "",
      assumptions: data?.assumptions || "",
      constraints: data?.constraints || "",
      stakeholdersInfo: data?.stakeholdersInfo || "",
      timelineMilestones: data?.timelineMilestones || "",
      successCriteria: data?.successCriteria || "",
      risksMitigation: data?.risksMitigation || "",
      changeManagementProcess: data?.changeManagementProcess || "",
      approvals: data?.approvals || "",
    },
    validate: zodResolver(clientDashboardFormSchema),
  });

  console.log("form values:", form.values);
  console.log("data from API:", data);

  if (isLoading) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  if (!data) {
    return <Center>No project found.</Center>;
  }

  const handleSubmit = (values: typeof form.values) => {
    updateProject({
      projectId: projectId!,
      userId: user._id,
      payload: values,
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Grid mt={2} gutter="md">
        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Project Overview"
            placeholder="Enter project overview"
            autosize
            minRows={2}
            {...form.getInputProps("projectOverview" as any)}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Project Objectives"
            placeholder="Enter project objectives"
            autosize
            minRows={2}
            {...form.getInputProps("projectObjectives" as any)}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Scope of Work"
            placeholder="Enter scope of work"
            autosize
            minRows={2}
            {...form.getInputProps("scopeOfWork" as any)}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Inclusions"
            placeholder="Enter inclusions"
            autosize
            minRows={2}
            {...form.getInputProps("inclusions" as any)}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Exclusions"
            placeholder="Enter exclusions"
            autosize
            minRows={2}
            {...form.getInputProps("exclusions" as any)}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Assumptions"
            placeholder="Enter assumptions"
            autosize
            minRows={2}
            {...form.getInputProps("assumptions" as any)}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Constraints"
            placeholder="Enter constraints"
            autosize
            minRows={2}
            {...form.getInputProps("constraints" as any)}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Stakeholders Info"
            placeholder="Enter stakeholders info"
            autosize
            minRows={2}
            {...form.getInputProps("stakeholdersInfo" as any)}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Timeline & Milestones"
            placeholder="Enter timeline & milestones"
            autosize
            minRows={2}
            {...form.getInputProps("timelineMilestones" as any)}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Success Criteria"
            placeholder="Enter success criteria"
            autosize
            minRows={2}
            {...form.getInputProps("successCriteria" as any)}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Risks & Mitigation"
            placeholder="Enter risks & mitigation"
            autosize
            minRows={2}
            {...form.getInputProps("risksMitigation" as any)}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Change Management Process"
            placeholder="Enter change management process"
            autosize
            minRows={2}
            {...form.getInputProps("changeManagementProcess" as any)}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            withAsterisk
            label="Approvals"
            placeholder="Enter approvals"
            autosize
            minRows={2}
            {...form.getInputProps("approvals" as any)}
          />
        </Grid.Col>
      </Grid>

      <Group position="right" spacing={20} mt={10}>
        <Button
          variant="outline"
          onClick={() => {
            navigate("/project-management");
          }}
        >
          Cancel
        </Button>
        <Button type="submit" variant="filled">
          Submit
        </Button>
      </Group>
    </form>
  );
}
